import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Edit, Trash2, Box } from "lucide-react";
import { Button } from "@shared/ui/button";
import { Card } from "@shared/ui/card";
import { Tabs } from "@shared/ui/tabs";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useMeterModelDetail, useDeleteMeterModel } from "@features/mx/manufacturer/hooks";
import GeneralInfoTab from "./tabs/GeneralInfo";
import TechnicalSpecificationTab from "./tabs/TechnicalSpecification";
import ResourcesTab from "./tabs/Resources";
import { ActivityLogTab } from "@features/mx/shared/components/ActivityLog";

export const MeterModelDetailLanding = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();

  const { data: modelDetail, isLoading } = useMeterModelDetail({
    remote_utility_id: remoteUtilityId,
    meter_model_id: id || "",
  });

  const { mutate: deleteModel } = useDeleteMeterModel();

  const handleDelete = () => {
    if (id) {
      deleteModel(id, {
        onSuccess: () => {
          navigate("/mx/settings?tab=manufacturer-models&subtab=models");
        },
      });
    }
  };

  const tabComponents = {
    generalInfo: {
      label: "General Information",
      shortLabel: "general-info",
      component: <GeneralInfoTab modelDetail={modelDetail} />,
    },
    techSpec: {
      label: "Technical Specifications",
      shortLabel: "tech-spec",
      component: <TechnicalSpecificationTab modelDetail={modelDetail} />,
    },
    resources: {
      label: "Resources",
      shortLabel: "resources",
      component: <ResourcesTab modelId={id || ""} />,
    },
    logs: {
      label: "Activity Log",
      shortLabel: "logs",
      component: <ActivityLogTab entityId={id} entityType="meter_model" />,
    },
  };

  if (isLoading) {
    return <div className="p-8 text-center text-gray-500">Loading model details...</div>;
  }

  const modelName = modelDetail?.result?.meterModel || "NA";
  const makeCode = modelDetail?.result?.meterMake || "NA";
  const status = modelDetail?.result?.is_active ?? true ? "Active" : "Inactive";
  const manufacturer = modelDetail?.result?.meterManufacturer?.name || "NA";
  const resourceType = modelDetail?.result?.resourceTypeDisplay || "NA";
  const resourceSubType = modelDetail?.result?.resourceSubtypeDisplay || "NA";

  return (
    <div className="space-y-6 p-4">
      {/* 1. Page Header */}
      <div className="flex items-center gap-4 mb-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-full hover:bg-gray-100"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-5 w-5 text-gray-700" />
        </Button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Model Details</h1>
          <p className="text-sm text-gray-500 font-medium">View complete meter model configuration</p>
        </div>
      </div>

      {/* 2. Main Grid Layout (Top Card & Quick Actions) */}
      <div className="grid grid-cols-12 gap-6">
        {/* Top Info Card */}
        <div className="col-span-12 lg:col-span-9">
          <Card className="p-6 border border-gray-200 shadow-sm flex items-center gap-5">
            <div className="h-16 w-16 rounded-xl bg-blue-50 flex items-center justify-center border border-blue-100 text-blue-600">
              <Box className="w-8 h-8" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-bold text-gray-950">{modelName}</h2>
              <span className="text-sm text-gray-500 block">{makeCode}</span>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${status === "Active"
                  ? "bg-green-50 text-green-700 border-green-200"
                  : "bg-gray-50 text-gray-600 border-gray-200"
                  }`}>
                  {status}
                </span>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100">
                  {manufacturer}
                </span>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-150">
                  {resourceType}
                </span>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-150">
                  {resourceSubType}
                </span>
              </div>
            </div>
          </Card>
        </div>

        {/* Quick Actions Panel */}
        <div className="col-span-12 lg:col-span-3">
          <Card>
            <h3 className="font-semibold text-lg text-gray-900 pb-2">Quick Actions</h3>
            <div className="space-y-3">
              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={() => navigate(`/mx/settings/models/edit/${id}`)}
              >
                <Edit className="h-4 w-4 text-gray-500" />
                <span className="font-medium text-sm">Edit Model</span>
              </Button>

              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={handleDelete}
              >
                <Trash2 className="h-4 w-4" />
                <span className="font-medium text-sm">Delete Model</span>
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {/* 3. Tabs Panel */}
      <div className="pt-4">
        <Tabs
          defaultValue="generalInfo"
          tabComponents={tabComponents}
          tabsListFullWidth={true}
          tabsListClassName="grid grid-cols-4"
          disableUrlSync={true}
        />
      </div>
    </div>
  );
};
